import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CommonModal from '../../components/common/CommonModal';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EnhancedTable from '../../components/common/EnhancedTable';
import { format, parseISO } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useClasses } from '../../hooks/useClasses';
import { useSessionClass } from '../../contexts/SessionClassContext';
import {
  TaskResponse,
  TaskPriority,
  TaskStatus,
  deleteTask,
  getTaskById,
  getTasksBySchool,
} from '../../api/tasks';
import { useError } from '../../contexts/ErrorContext';
import CreateTaskDialog from './CreateTaskDialog';
import ActionButtons from '../../components/common/ActionButtons';

type StatusFilter = TaskStatus | 'all' | 'late';

const priorityColor = (priority: TaskPriority): 'default' | 'info' | 'warning' | 'error' => {
  switch (priority) {
    case 'HIGH':
      return 'error';
    case 'MEDIUM':
      return 'info';
    case 'LOW':
    default:
      return 'default';
  }
};

const statusColor = (status: TaskStatus): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'IN_PROGRESS':
      return 'info';
    case 'PENDING':
      return 'warning';
    case 'CANCELLED':
      return 'error';
    default:
      return 'default';
  }
};

const hasLateSubmission = (task: TaskResponse) =>
  (task.assignees ?? []).some((assignee) => assignee.assigneeStatus === 'COMPLETED' && assignee.late);

const statusChip = (task: TaskResponse) => {
  if (hasLateSubmission(task)) {
    return <Chip label="LATE SUBMISSION" size="small" color="error" />;
  }

  const effectiveStatus = getEffectiveStatus(task);
  return (
    <Chip
      label={String(effectiveStatus).replace('_', ' ')}
      size="small"
      color={statusColor(effectiveStatus)}
    />
  );
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    return format(parseISO(value), 'dd MMM yyyy');
  } catch {
    return value;
  }
};

/**
 * Computes the effective status of a task based on its assignees' individual statuses.
 * - If ALL assignees have completed → COMPLETED
 * - If SOME assignees have completed → IN_PROGRESS
 * - Otherwise → use task's own status (PENDING/CANCELLED etc.)
 */
const getEffectiveStatus = (task: TaskResponse): TaskStatus => {
  const assignees = task.assignees;
  if (assignees && assignees.length > 0) {
    const completedCount = assignees.filter((a) => a.assigneeStatus === 'COMPLETED').length;
    if (completedCount === assignees.length) return 'COMPLETED';
    if (completedCount > 0) return 'IN_PROGRESS';
  }
  return task.status;
};

const TaskAssignmentPage: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const queryClient = useQueryClient();
  const location = useLocation();
  const schoolId = user?.schoolId;

  const { selectedClass } = useSessionClass();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState<TaskResponse | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);

  const { classesQuery } = useClasses(schoolId ?? undefined);
  const classes = useMemo(() => classesQuery.data ?? [], [classesQuery.data]);

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'school', schoolId],
    queryFn: async () => {
      const response = await getTasksBySchool(schoolId!);
      return response.data;
    },
    enabled: !!schoolId,
    // Poll every 30 seconds so principal sees teacher updates automatically
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  React.useEffect(() => {
    const taskId = (location.state as { taskId?: number } | null)?.taskId;
    if (taskId) {
      getTaskById(taskId)
        .then((res) => {
          setSelectedTask(res.data);
          setDrawerOpen(true);
        })
        .catch(() => showError('Failed to load task details'));
    }
  }, [location.state, showError]);

  const filteredTasks = useMemo(() => {
    let rows = tasksQuery.data ?? [];

    // Filter by the class selected in the top navbar (selectedClass from SessionClassContext)
    if (!selectedClass || selectedClass.section === "__CLASS_ALL__" || selectedClass.id === undefined) {
      // no filter
    } else if (selectedClass.section === "__GRADE_ALL__") {
      // Filter by grade name. A task matches if its class name matches selectedClass.name.
      rows = rows.filter((task) => {
        const cls = classes.find((c) => c.id === task.classId);
        if (cls) {
          return cls.name.trim().toLowerCase() === selectedClass.name.trim().toLowerCase();
        }
        const nameLower = selectedClass.name.trim().toLowerCase();
        return task.className?.trim().toLowerCase().startsWith(nameLower);
      });
    } else {
      // Specific class selected (e.g. 1st A)
      rows = rows.filter((task) => task.classId === selectedClass.id);
    }

    if (statusFilter === 'all') {
      rows = rows.filter((task) => task.status !== 'CANCELLED');
    } else if (statusFilter === 'late') {
      rows = rows.filter(hasLateSubmission);
    } else {
      rows = rows.filter((task) => task.status === statusFilter);
    }
    return rows;
  }, [tasksQuery.data, selectedClass, statusFilter, classes]);

  const refetchTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const openCreateDialog = () => {
    setEditTask(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (task: TaskResponse) => {
    try {
      const response = await getTaskById(task.id);
      setEditTask(response.data);
      setDialogOpen(true);
    } catch {
      showError('Failed to load task for editing');
    }
  };

  const openDetailDrawer = async (task: TaskResponse) => {
    try {
      const response = await getTaskById(task.id);
      setSelectedTask(response.data);
      setDrawerOpen(true);
    } catch {
      showError('Failed to load task details');
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask(taskToDelete);
      showSuccess('Task deleted successfully');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      refetchTasks();
      if (selectedTask?.id === taskToDelete) {
        setDrawerOpen(false);
        setSelectedTask(null);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Failed to delete task');
    }
  };

  const columns = [
    { id: 'title', label: 'Title', minWidth: 160 },
    { id: 'className', label: 'Class', minWidth: 100 },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value: any, row: TaskResponse) => {
        return statusChip(row);
      },
    },
    {
      id: 'assigneeCount',
      label: 'Assignees',
      minWidth: 90,
      align: 'center' as const,
    },
  ];

  const renderActions = (row: TaskResponse) => (
    <ActionButtons
      showView
      onView={() => openDetailDrawer(row)}
      onEdit={() => openEditDialog(row)}
      onDelete={() => {
        setTaskToDelete(row.id);
        setDeleteDialogOpen(true);
      }}
    />
  );

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: "1.25rem",
              lineHeight: 1.2,
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            Task Assignment
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 36 } }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="late">Late Submission</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "#1976d2" },
                height: 36,
              }}
              onClick={openCreateDialog}
            >
              Create Task
            </Button>
          </Box>
        </Box>

        {tasksQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <EnhancedTable
            columns={columns}
            data={filteredTasks}
            searchFields={[]}
            actions={renderActions}
            cardProps={{
              elevation: 0,
              sx: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 0,
                '& .MuiCardContent-root': {
                  p: 0,
                  '&:last-child': { pb: 0 }
                }
              },
            }}
          />
        )}
      </Paper>

      <CreateTaskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={refetchTasks}
        editTask={editTask}
      />

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Task Details
          </Typography>
          {selectedTask ? (
            <>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedTask.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                {selectedTask.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={selectedTask.priority} size="small" color={priorityColor(selectedTask.priority)} />
                {statusChip(selectedTask)}
              </Box>
              <Typography variant="body2">Class: {selectedTask.className}</Typography>
              <Typography variant="body2">Start: {formatDate(selectedTask.startDate)}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                End: {formatDate(selectedTask.endDate)}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assignees
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Read</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedTask.assignees ?? []).map((assignee) => (
                    <TableRow key={assignee.id}>
                      <TableCell>{assignee.teacherName}</TableCell>
                      <TableCell>{assignee.subjectName || '-'}</TableCell>
                      <TableCell>
                        {assignee.assigneeStatus === 'COMPLETED' && assignee.late
                          ? 'LATE SUBMISSION'
                          : assignee.assigneeStatus}
                      </TableCell>
                      <TableCell>{assignee.isRead ?? (assignee as { read?: boolean }).read ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button sx={{ mt: 3 }} variant="outlined" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </>
          ) : (
            <CircularProgress size={24} />
          )}
        </Box>
      </Drawer>

      <CommonModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Task"
        maxWidth="xs"
        danger
        onSubmit={handleDelete}
        submitLabel="Delete"
        cancelLabel="Cancel"
      >
        <Typography>Are you sure you want to delete this task?</Typography>
      </CommonModal>
    </>
  );
};

export default TaskAssignmentPage;
